/**
 * Каталог декора: ковры, картины, растения, вещи питомцев, книги, настольная лампа
 * и проекционный экран. Ноутбук и монитор — техника, они в разделе «Мебель». Всё живёт в слое мебели. Ковры лежат под диваном и кроватью,
 * поэтому на плане рисуются раньше остальных предметов; высота ковра — толщина ворса, 12 мм.
 * Картина и экран вешаются на стену — `elevation` у них высота центра. Лампа,
 * книги и настольный цветок стоят на столе: `elevation` 750 — высота столешницы.
 * Id — ключи ссылок из слоя, менять нельзя.
 */
import { LayerKind } from "@/shared/plan/layerKind";
import { CatalogAttach, type CatalogSection, type View3D } from "../../types";
import { DecorId } from "./ids";
import { RugShape, type DecorCatalogItem } from "./types";

/** Ковёр строится кодом и не искажается — от прикроватного до большого в гостиную. */
const RUG_LIMITS = {
	width: { min: 600, max: 4000 },
	depth: { min: 600, max: 5000 },
};

/** Высота столешницы, мм: на ней стоят лампа и книги */
const DESK_TOP = 750;

/** Модель, собранная кодом, в габаритах предмета — чтобы не повторять размеры дважды. */
const model = (
	id: string,
	{ width, height, depth, color }: { width: number; height: number; depth: number; color: string },
	tintable = false,
): View3D => ({
	type: "component",
	id,
	width,
	height,
	depth,
	color,
	...(tintable ? { tintable } : {}),
});

/** Предмет декора: габарит, цвет и модель кодом с тем же id. */
const item = (
	fields: Omit<DecorCatalogItem, "view3d" | "attach"> & { attach?: CatalogAttach },
	tintable = false,
): DecorCatalogItem => ({
	...fields,
	attach: fields.attach ?? CatalogAttach.Everywhere,
	view3d: model(fields.id, fields, tintable),
});

/** Предмет с моделью-файлом из `public/models/decor`: габарит модели — габарит предмета.
 *  Без `elevation` предмет стоит на полу, без `attach` — ставится куда угодно. */
const glb = (
	fields: Omit<DecorCatalogItem, "view3d" | "attach" | "elevation"> & {
		elevation?: number;
		attach?: CatalogAttach;
	},
): DecorCatalogItem => ({
	...fields,
	elevation: fields.elevation ?? 0,
	attach: fields.attach ?? CatalogAttach.Everywhere,
	view3d: {
		type: "model",
		url: `/models/decor/${fields.id}.glb`,
		width: fields.width,
		height: fields.height,
		depth: fields.depth,
		color: fields.color,
	},
});

export const decorSection: CatalogSection<DecorCatalogItem> = {
	kind: LayerKind.Furniture,
	label: "Декор",
	canChangeDimensions: true,
	subGroups: [
		"Ковры",
		"Картины",
		"Растения",
		"Питомцы",
		"Книги",
		"Рабочее место",
		"Хобби",
		"Кинотеатр",
		"Подушки",
		"Лампы",
		"Интерьер",
	],
	families: [
		{ id: "rug", name: "Ковёр", defaultItem: DecorId.RugRect },
		{ id: "plant-floor", name: "Растение в кашпо", defaultItem: DecorId.PlantMonstera },
		{ id: "painting", name: "Картина", defaultItem: DecorId.Painting },
		{ id: "books", name: "Книги", defaultItem: DecorId.BooksRow },
		{ id: "guitar", name: "Гитара", defaultItem: DecorId.Guitar },
		{ id: "cushion", name: "Подушка декоративная", defaultItem: DecorId.CushionPlaid },
		{ id: "table-lamp", name: "Лампа настольная", defaultItem: DecorId.TableLamp },
		{ id: "dog-bed", name: "Лежанка", defaultItem: DecorId.DogBed },
		{ id: "laundry-basket", name: "Корзина для белья", defaultItem: DecorId.LaundryBasket },
	],
	items: [
		{
			id: DecorId.RugRect,
			family: "rug",
			sizeLimits: RUG_LIMITS,
			subGroup: "Ковры",
			name: "Ковёр прямоугольный",
			description: "Ковёр 2000 × 3000 с каймой и бахромой по коротким сторонам.",
			width: 2000,
			height: 12,
			depth: 3000,
			elevation: 0,
			color: "#c8b8a0",
			attach: CatalogAttach.Everywhere,
			rug: { shape: RugShape.Rect, border: "#7a5f49" },
			view3d: {
				type: "component",
				id: DecorId.RugRect,
				width: 2000,
				height: 12,
				depth: 3000,
				color: "#c8b8a0",
				tintable: true,
			},
		},
		{
			id: DecorId.RugRound,
			family: "rug",
			sizeLimits: RUG_LIMITS,
			subGroup: "Ковры",
			name: "Ковёр круглый",
			description: "Круглый ковёр ⌀ 2000 с тонкой каймой.",
			width: 2000,
			height: 12,
			depth: 2000,
			elevation: 0,
			color: "#9aa5a0",
			attach: CatalogAttach.Everywhere,
			rug: { shape: RugShape.Round, border: "#5d6964" },
			view3d: {
				type: "component",
				id: DecorId.RugRound,
				width: 2000,
				height: 12,
				depth: 2000,
				color: "#9aa5a0",
				tintable: true,
			},
		},
		item({
			id: DecorId.Painting,
			family: "painting",
			subGroup: "Картины",
			name: "Картина в раме",
			description: "Пейзаж 800 × 600 в деревянной раме. Центр — на высоте 1500 мм.",
			width: 800,
			height: 600,
			depth: 30,
			elevation: 1500,
			color: "#5b4636",
			attach: CatalogAttach.OnlyWall,
		}),
		item({
			id: DecorId.PaintingWide,
			family: "painting",
			subGroup: "Картины",
			name: "Картина широкая",
			description: "Горизонтальное полотно 1200 × 500 в тонкой раме, над диваном или кроватью.",
			width: 1200,
			height: 500,
			depth: 25,
			elevation: 1600,
			color: "#2b2b2b",
			attach: CatalogAttach.OnlyWall,
		}),
		{
			id: DecorId.PlantMonstera,
			family: "plant-floor",
			round: true,
			subGroup: "Растения",
			name: "Монстера в кашпо",
			description: "Большая монстера с резными листьями в белом керамическом кашпо, высота 1000.",
			width: 900,
			height: 1000,
			depth: 900,
			elevation: 0,
			color: "#4f7d3b",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/decor/plant-monstera.glb",
				width: 900,
				height: 1000,
				depth: 900,
				color: "#4f7d3b",
			},
		},
		item(
			{
				id: DecorId.CatFountain,
				round: true,
				subGroup: "Питомцы",
				name: "Поилка-фонтан",
				description: "Поилка-фонтан ⌀ 200 с чашей и струйкой воды, нужна розетка рядом.",
				width: 200,
				height: 180,
				depth: 200,
				elevation: 0,
				color: "#f1f3f5",
			},
			true,
		),
		item(
			{
				id: DecorId.CatLitter,
				subGroup: "Питомцы",
				name: "Лоток для кота",
				description: "Открытый лоток 400 × 550 с бортами 150 мм и наполнителем.",
				width: 400,
				height: 150,
				depth: 550,
				elevation: 0,
				color: "#7fb3a8",
			},
			true,
		),
		item({
			id: DecorId.BooksStack,
			family: "books",
			subGroup: "Книги",
			name: "Стопка книг",
			description: "Четыре книги стопкой, лежат на столе или тумбе.",
			width: 300,
			height: 160,
			depth: 220,
			elevation: DESK_TOP,
			color: "#8c4a3c",
		}),
		item({
			id: DecorId.BooksRow,
			family: "books",
			subGroup: "Книги",
			name: "Книги в ряд",
			description: "Ряд книг 450 мм между двумя держателями, на полке или столе.",
			width: 450,
			height: 250,
			depth: 180,
			elevation: DESK_TOP,
			color: "#3f5f7f",
		}),
		item(
			{
				id: DecorId.DeskLamp,
				subGroup: "Рабочее место",
				name: "Лампа настольная",
				description: "Лампа на круглом основании с шарнирной ножкой и плафоном-конусом.",
				width: 160,
				height: 450,
				depth: 300,
				elevation: DESK_TOP,
				color: "#2f3a45",
			},
			true,
		),
		{
			id: DecorId.Guitar,
			family: "guitar",
			subGroup: "Хобби",
			name: "Гитара акустическая на стойке",
			description: "Акустическая гитара на напольной стойке, высота 1050.",
			width: 420,
			height: 1050,
			depth: 280,
			elevation: 0,
			color: "#c49a6c",
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/decor/guitar.glb",
				width: 420,
				height: 1050,
				depth: 280,
				color: "#c49a6c",
			},
		},
		item({
			id: DecorId.ProjectorScreen,
			subGroup: "Кинотеатр",
			name: "Экран для проектора",
			description:
				"Настенный рулонный экран 2000 × 1250 в корпусе, белое полотно с чёрной каймой. Центр — на 1600 мм.",
			width: 2000,
			height: 1250,
			depth: 80,
			elevation: 1600,
			color: "#1f2328",
			attach: CatalogAttach.OnlyWall,
		}),
		// Модели-файлы второй партии: смотрят передом в +Z, размеры — по пропорциям модели
		glb({
			id: DecorId.PlantFicus,
			family: "plant-floor",
			round: true,
			subGroup: "Растения",
			name: "Фикус лировидный",
			description: "Фикус-деревце высотой 1600 в белом кашпо.",
			width: 910,
			height: 1600,
			depth: 750,
			color: "#3f6b35",
		}),
		glb({
			id: DecorId.PlantSnake,
			family: "plant-floor",
			round: true,
			subGroup: "Растения",
			name: "Сансевиерия",
			description: "Сансевиерия высотой 900 в сером горшке.",
			width: 450,
			height: 900,
			depth: 425,
			color: "#4f6b3a",
		}),
		glb({
			id: DecorId.DogBed,
			family: "dog-bed",
			round: true,
			subGroup: "Питомцы",
			name: "Лежанка для собаки",
			description: "Круглая плюшевая лежанка ⌀ 800 с мягким бортом.",
			width: 800,
			height: 210,
			depth: 800,
			color: "#8d8d8f",
		}),
		glb({
			id: DecorId.GuitarElectric,
			family: "guitar",
			subGroup: "Хобби",
			name: "Гитара электрическая с комбиком",
			description: "Электрогитара на стойке и небольшой комбоусилитель рядом, высота 1050.",
			width: 725,
			height: 1050,
			depth: 430,
			color: "#b0561e",
		}),
		glb({
			id: DecorId.Easel,
			subGroup: "Хобби",
			name: "Мольберт с холстом",
			description: "Деревянный мольберт-тренога с холстом, высота 1700.",
			width: 765,
			height: 1700,
			depth: 730,
			color: "#b98a52",
		}),
		glb({
			id: DecorId.VaseFloor,
			round: true,
			subGroup: "Интерьер",
			name: "Ваза напольная с сухоцветом",
			description: "Бежевая керамическая ваза с пампасной травой, высота 1100.",
			width: 540,
			height: 1100,
			depth: 650,
			color: "#cdb89a",
		}),
		glb({
			id: DecorId.MirrorFloor,
			subGroup: "Интерьер",
			name: "Зеркало напольное",
			description: "Арочное зеркало 560 × 1700 в тонкой чёрной раме, приставлено к стене.",
			width: 560,
			height: 1700,
			depth: 290,
			color: "#2b2b2d",
		}),
		glb({
			id: DecorId.Fireplace,
			subGroup: "Интерьер",
			name: "Электрокамин",
			description: "Электрокамин в белом портале 1200 × 400, высота 925. Нужна розетка рядом.",
			width: 1200,
			height: 925,
			depth: 400,
			color: "#e6e4df",
		}),
		glb({
			id: DecorId.LaundryBasket,
			family: "laundry-basket",
			round: true,
			subGroup: "Интерьер",
			name: "Корзина для белья",
			description: "Плетёная корзина из водоросли ⌀ 500, высота 420.",
			width: 500,
			height: 420,
			depth: 500,
			color: "#b8955f",
		}),
		// Пятая партия: подушки лежат на полу или диване, лампы стоят на столе,
		// часы висят на стене (`elevation` — высота центра)
		glb({
			id: DecorId.CushionPlaid,
			family: "cushion",
			subGroup: "Подушки",
			name: "Подушка в клетку",
			description: "Декоративная подушка 450 × 450 в клетку.",
			width: 450,
			height: 425,
			depth: 410,
			color: "#af9e68",
		}),
		glb({
			id: DecorId.CushionWoven,
			family: "cushion",
			subGroup: "Подушки",
			name: "Подушка плетёная",
			description: "Декоративная подушка 450 × 420 с плетёным узором.",
			width: 450,
			height: 420,
			depth: 180,
			color: "#573d33",
		}),
		glb({
			id: DecorId.CushionBaroque,
			family: "cushion",
			subGroup: "Подушки",
			name: "Подушка барочная",
			description: "Плоская подушка 500 × 405 с барочным узором.",
			width: 500,
			height: 150,
			depth: 405,
			color: "#291919",
		}),
		glb({
			id: DecorId.CushionRed,
			family: "cushion",
			subGroup: "Подушки",
			name: "Подушка бордовая",
			description: "Декоративная подушка 450 × 450 бордового цвета.",
			width: 450,
			height: 450,
			depth: 210,
			color: "#3f1a16",
		}),
		glb({
			id: DecorId.CushionRhombus,
			family: "cushion",
			subGroup: "Подушки",
			name: "Подушка с ромбами",
			description: "Декоративная подушка 450 × 380 с ромбовидным узором.",
			width: 450,
			height: 380,
			depth: 170,
			color: "#b5865c",
		}),
		glb({
			id: DecorId.TableLamp,
			family: "table-lamp",
			round: true,
			subGroup: "Лампы",
			name: "Лампа с абажуром",
			description: "Настольная лампа ⌀ 300 с тканевым абажуром, высота 510.",
			width: 300,
			height: 510,
			depth: 300,
			elevation: DESK_TOP,
			color: "#9c8467",
		}),
		glb({
			id: DecorId.DeskLampModern,
			family: "table-lamp",
			subGroup: "Лампы",
			name: "Лампа современная",
			description: "Настольная лампа 330 × 325 современной формы, высота 450.",
			width: 330,
			height: 450,
			depth: 325,
			elevation: DESK_TOP,
			color: "#b5957b",
		}),
		glb({
			id: DecorId.LampWood,
			family: "table-lamp",
			round: true,
			subGroup: "Лампы",
			name: "Лампа на деревянной ножке",
			description: "Узкая настольная лампа ⌀ 180 на деревянной ножке, высота 600.",
			width: 180,
			height: 600,
			depth: 180,
			elevation: DESK_TOP,
			color: "#755b21",
		}),
		glb({
			id: DecorId.PetBedDonut,
			family: "dog-bed",
			round: true,
			subGroup: "Питомцы",
			name: "Лежанка-пончик",
			description: "Круглая лежанка ⌀ 800 с высоким мягким бортом.",
			width: 800,
			height: 185,
			depth: 790,
			color: "#7c7369",
		}),
		glb({
			id: DecorId.LaundryBasket2,
			family: "laundry-basket",
			round: true,
			subGroup: "Интерьер",
			name: "Корзина для белья тёмная",
			description: "Плетёная корзина ⌀ 400, высота 540.",
			width: 400,
			height: 540,
			depth: 400,
			color: "#54402f",
		}),
		glb({
			id: DecorId.TableOrganic,
			subGroup: "Интерьер",
			name: "Консоль дизайнерская",
			description: "Консоль 1200 × 445 из дерева плавной формы, высота 1090.",
			width: 1200,
			height: 1090,
			depth: 445,
			color: "#97774e",
		}),
		glb({
			id: DecorId.ClockWall,
			subGroup: "Интерьер",
			name: "Часы настенные",
			description: "Круглые часы ⌀ 400 в деревянной раме. Центр — на высоте 1800 мм.",
			width: 400,
			height: 400,
			depth: 125,
			elevation: 1800,
			color: "#c69c6c",
			attach: CatalogAttach.OnlyWall,
		}),
	],
};
